// «Mis peticiones» (F2 del sistema de peticiones, 11-oct-2026). Lo que el estudio le ha pedido a
// Lealify (arreglos, mejoras, dudas) con número, estado y lo que se hizo. Mismo backend que la agenda
// dental: lealify-base/backend/routes/client-requests.js, montado en /api/client-requests (NÚCLEO, no
// /api/letsmove → por eso no pasa por apiClient.baseFor). El cliente LEE, CREA y RESPONDE; los cambios
// de estado los hace Lealify (forobeta-ops/scripts/peticion.py). Copia de agenda/src/services/clientRequestsService.ts.

const BASE = '/api/client-requests';

export type RequestStatus = 'nueva' | 'revision' | 'curso' | 'esperando' | 'hecha' | 'descartada';
export type RequestKind = 'bug' | 'mejora' | 'duda' | 'dato' | 'otro';

export interface ClientRequest {
  id: number;
  kind: RequestKind;
  kindLabel: string;
  area: string | null;
  status: RequestStatus;
  statusLabel: string;
  title: string;
  detail: string | null;
  resolution: string | null;
  doc_url: string | null;
  attachment_url: string | null;
  requested_by: string | null;
  requested_at: string | null;
  eta_date: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface ClientRequestEvent {
  at: string;
  actor: string;
  to_status: RequestStatus | null;
  statusLabel: string | null;
  note: string | null;
}

export interface ClientRequestDetail extends ClientRequest { events: ClientRequestEvent[] }

/** Avisa al menú que cambió algo (para refrescar el globo de «esperando tu respuesta»). */
export const CLIENT_REQUESTS_EVENT = 'lm:peticiones-cambio';

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data && data.error) || `Error ${res.status}`);
  return data as T;
}

export async function listClientRequests(estado: 'abiertas' | 'hechas' | 'todas' = 'abiertas'): Promise<ClientRequest[]> {
  const r = await send<{ data: ClientRequest[] }>('GET', `?estado=${estado}`);
  return r.data || [];
}

export async function getClientRequest(id: number): Promise<ClientRequestDetail> {
  const r = await send<{ data: ClientRequestDetail }>('GET', `/${id}`);
  return r.data;
}

export async function getClientRequestsCount(): Promise<{ esperando: number; abiertas: number; puede?: boolean }> {
  return send('GET', '/count');
}

export async function createClientRequest(input: { title: string; detail?: string; kind?: RequestKind; attachmentUrl?: string | null }): Promise<ClientRequest> {
  const r = await send<{ data: ClientRequest }>('POST', '', input);
  window.dispatchEvent(new Event(CLIENT_REQUESTS_EVENT));
  return r.data;
}

export async function replyClientRequest(id: number, text: string): Promise<{ status: RequestStatus; statusLabel: string }> {
  const r = await send<{ status: RequestStatus; statusLabel: string }>('POST', `/${id}/responder`, { text });
  window.dispatchEvent(new Event(CLIENT_REQUESTS_EVENT));
  return r;
}

/** Tipos y tope que acepta el uploader de api.lealify.com (multer: image/jpeg|png|webp, 5 MB). */
export const CAPTURA_TIPOS = ['image/jpeg', 'image/png', 'image/webp'];
export const CAPTURA_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Sube la captura al uploader público (api.lealify.com) y devuelve su URL absoluta — la API de
 * peticiones SOLO acepta adjuntos alojados ahí. Mismo flujo que la agenda (imagenPublicaService):
 * token HMAC corto con la sesión (GET /api/uploads/token) → POST multipart con el campo `image`
 * (el uploader hace upload.single('image'); otro nombre de campo lo rechaza).
 */
export async function subirCaptura(file: File): Promise<string> {
  const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  const tokenResp = await fetch('/api/uploads/token', { credentials: 'include' });
  if (!tokenResp.ok) throw new Error('No se pudo autorizar la subida de la captura');
  const { token } = await tokenResp.json();
  const form = new FormData();
  form.append('image', file, `peticion-${Date.now()}.${ext}`);
  const resp = await fetch('https://api.lealify.com/upload', { method: 'POST', headers: { 'x-upload-token': token }, body: form });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data?.success || !data?.url) throw new Error(data?.error || 'No se pudo subir la captura');
  return String(data.url).startsWith('http') ? data.url : `https://api.lealify.com${data.url}`;
}
